import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAccount, useChainId, useChains, useReadContract } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useQuery } from '@tanstack/react-query';
import { factoryAbi } from '../abi/factory';
import { getFactoryAddress } from '../config/factories';
import { SplitterPanel } from '../components/SplitterPanel';
import { SolanaSplitActivity } from '../components/SolanaSplitActivity';
import { SuiSplitActivity } from '../components/SuiSplitActivity';
import {
  loadDeployedSplits,
  removeDeployedSplit,
  suppressOnChainSplit,
  loadSuppressedOnChainSplits,
} from '../split/storage';
import {
  discoverSolanaSplitsForPayee,
  discoverSuiSplitObjectIdsForCreator,
  resolveSuiSplitObjectIdFromDeployDigest,
  clusterFromSolanaRpcUrl,
  clusterFromSuiRpcUrl,
  normalizeSuiAddress,
} from '../split/chainDiscover';
import { getSolanaAddress, getSuiAddress, subscribeSolanaAccountChanged } from '../split/walletConnect';
import { getChainIconSrc } from '../config/chainDisplay';
import { assetUrl, brand } from '../config/brand';

/** false: hide Solana/Sui dashboard blocks until programs are deployed. Set to true to restore. */
const SHOW_SOLANA_SUI = false;

const SOLANA_PROGRAM_ID = process.env.REACT_APP_SOLANA_PROGRAM_ID ?? '';
const SOLANA_RPC_URL = process.env.REACT_APP_SOLANA_RPC_URL ?? '';
const SUI_PACKAGE_ID = process.env.REACT_APP_SUI_PACKAGE_ID ?? '';
const SUI_RPC_CONFIGURED = process.env.REACT_APP_SUI_RPC_URL ?? 'https://fullnode.devnet.sui.io:443';
const SUI_MAINNET_RPC = 'https://fullnode.mainnet.sui.io:443';

function suiRowKey(network: 'devnet' | 'mainnet', objectId?: string, deployDigest?: string) {
  if (objectId) return `${network}:o:${normalizeSuiAddress(objectId)}`;
  if (deployDigest) return `${network}:d:${deployDigest}`;
  return `${network}:empty`;
}

function suiRpcForNetwork(network: 'devnet' | 'mainnet') {
  if (network === 'mainnet') return SUI_MAINNET_RPC;
  if (clusterFromSuiRpcUrl(SUI_RPC_CONFIGURED) === 'devnet') return SUI_RPC_CONFIGURED;
  return 'https://fullnode.devnet.sui.io:443';
}

type SolanaDashRow = {
  addr: string;
  cluster: 'devnet' | 'mainnet';
  tags: ('saved' | 'wallet')[];
};

type SuiDashRow = {
  network: 'devnet' | 'mainnet';
  objectId?: string;
  deployDigest?: string;
  tags: ('saved' | 'wallet')[];
};

export function Dashboard() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const chains = useChains();
  const chain = useMemo(() => chains.find((c) => c.id === chainId), [chains, chainId]);
  const factory = getFactoryAddress(chainId);

  const { data: splitters, isLoading } = useReadContract({
    address: factory,
    abi: factoryAbi,
    functionName: 'splittersForPayee',
    args: address ? [address] : undefined,
    query: { enabled: !!factory && !!address },
  });

  const unique = useMemo(() => {
    const list = splitters ?? [];
    return Array.from(new Set(list.map((a) => String(a).toLowerCase()))).map((a) => a as `0x${string}`);
  }, [splitters]);

  const explorer = chain?.blockExplorers?.default?.url;
  const supportedChains = useMemo(
    () =>
      chains
        .map((c) => ({ id: c.id, name: c.name, icon: getChainIconSrc(c.id) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [chains]
  );

  const [listVersion, setListVersion] = useState(0);
  const [solAddr, setSolAddr] = useState<string | null>(() => getSolanaAddress());
  const [suiAddr, setSuiAddr] = useState<string | null>(null);

  useEffect(() => {
    if (!SHOW_SOLANA_SUI) return undefined;
    const sync = () => {
      setSolAddr(getSolanaAddress());
      void getSuiAddress().then(setSuiAddr);
    };
    sync();
    const iv = setInterval(sync, 3000);
    const off = subscribeSolanaAccountChanged(sync);
    return () => {
      clearInterval(iv);
      off();
    };
  }, []);

  const solanaConfiguredCluster = SOLANA_RPC_URL ? clusterFromSolanaRpcUrl(SOLANA_RPC_URL) : null;

  const { data: discoveredSolana = [] } = useQuery({
    queryKey: ['discover-solana-splits-for-payee', solAddr, SOLANA_PROGRAM_ID, SOLANA_RPC_URL],
    queryFn: () => discoverSolanaSplitsForPayee(solAddr!, SOLANA_PROGRAM_ID, SOLANA_RPC_URL),
    enabled: SHOW_SOLANA_SUI && Boolean(solAddr && SOLANA_PROGRAM_ID && SOLANA_RPC_URL),
    staleTime: 45_000,
  });

  const mergedSolana = useMemo((): SolanaDashRow[] => {
    if (!SHOW_SOLANA_SUI) return [];
    void listVersion;
    const map = new Map<string, SolanaDashRow>();

    const add = (addr: string, cluster: 'devnet' | 'mainnet', tag: 'saved' | 'wallet') => {
      if (loadSuppressedOnChainSplits('solana', cluster).has(addr.toLowerCase())) return;
      const k = `${cluster}:${addr.toLowerCase()}`;
      const ex = map.get(k);
      if (ex) {
        if (!ex.tags.includes(tag)) ex.tags.push(tag);
      } else {
        map.set(k, { addr, cluster, tags: [tag] });
      }
    };

    for (const a of loadDeployedSplits('solana', 'devnet')) add(a, 'devnet', 'saved');
    for (const a of loadDeployedSplits('solana', 'mainnet')) add(a, 'mainnet', 'saved');

    if (solAddr && solanaConfiguredCluster && discoveredSolana.length > 0) {
      for (const addr of discoveredSolana) add(addr, solanaConfiguredCluster, 'wallet');
    }

    return Array.from(map.values()).sort((a, b) => `${a.cluster}:${a.addr}`.localeCompare(`${b.cluster}:${b.addr}`));
  }, [listVersion, solAddr, solanaConfiguredCluster, discoveredSolana]);

  const hasSavedSuiSplits = useMemo(() => {
    if (!SHOW_SOLANA_SUI) return false;
    void listVersion;
    return (
      loadDeployedSplits('sui', 'devnet').length > 0 || loadDeployedSplits('sui', 'mainnet').length > 0
    );
  }, [listVersion]);

  const { data: mergedSui = [], isLoading: suiDiscoverLoading } = useQuery({
    queryKey: ['discover-sui-dashboard', suiAddr, SUI_PACKAGE_ID, SUI_RPC_CONFIGURED, listVersion],
    queryFn: async (): Promise<SuiDashRow[]> => {
      const map = new Map<string, SuiDashRow>();

      const add = (row: Omit<SuiDashRow, 'tags'> & { tag: 'saved' | 'wallet' }) => {
        const sup = loadSuppressedOnChainSplits('sui', row.network);
        const suppressId = (row.objectId ?? row.deployDigest ?? '').toLowerCase();
        if (!suppressId || sup.has(suppressId)) return;
        const key = suiRowKey(row.network, row.objectId, row.deployDigest);
        const ex = map.get(key);
        if (ex) {
          if (!ex.tags.includes(row.tag)) ex.tags.push(row.tag);
          if (row.objectId && !ex.objectId) ex.objectId = row.objectId;
          if (row.deployDigest && !ex.deployDigest) ex.deployDigest = row.deployDigest;
        } else {
          map.set(key, {
            network: row.network,
            objectId: row.objectId,
            deployDigest: row.deployDigest,
            tags: [row.tag],
          });
        }
      };

      const ingestSaved = async (network: 'devnet' | 'mainnet') => {
        const rpc = suiRpcForNetwork(network);
        for (const digest of loadDeployedSplits('sui', network)) {
          if (loadSuppressedOnChainSplits('sui', network).has(digest.toLowerCase())) continue;
          const resolved = await resolveSuiSplitObjectIdFromDeployDigest(digest, rpc);
          add({
            network,
            objectId: resolved ?? undefined,
            deployDigest: digest,
            tag: 'saved',
          });
        }
      };

      if (!SUI_PACKAGE_ID) {
        await ingestSaved('devnet');
        await ingestSaved('mainnet');
        return Array.from(map.values()).sort((a, b) =>
          suiRowKey(a.network, a.objectId, a.deployDigest).localeCompare(
            suiRowKey(b.network, b.objectId, b.deployDigest)
          )
        );
      }

      const configuredRpc = SUI_RPC_CONFIGURED;
      const configuredNet = clusterFromSuiRpcUrl(configuredRpc);

      let discovered: string[] = [];
      if (suiAddr) {
        discovered = await discoverSuiSplitObjectIdsForCreator(suiAddr, SUI_PACKAGE_ID, configuredRpc);
      }
      const discoveredNorm = new Set(discovered.map((x) => normalizeSuiAddress(x)));

      for (const oid of discovered) {
        if (loadSuppressedOnChainSplits('sui', configuredNet).has(oid.toLowerCase())) continue;
        add({ network: configuredNet, objectId: oid, tag: 'wallet' });
      }

      for (const network of ['devnet', 'mainnet'] as const) {
        const rpc = suiRpcForNetwork(network);
        for (const digest of loadDeployedSplits('sui', network)) {
          if (loadSuppressedOnChainSplits('sui', network).has(digest.toLowerCase())) continue;
          const resolved = await resolveSuiSplitObjectIdFromDeployDigest(digest, rpc);
          const resNorm = resolved ? normalizeSuiAddress(resolved) : '';
          if (network === configuredNet && resNorm && discoveredNorm.has(resNorm)) continue;
          add({
            network,
            objectId: resolved ?? undefined,
            deployDigest: digest,
            tag: 'saved',
          });
        }
      }

      return Array.from(map.values()).sort((a, b) =>
        suiRowKey(a.network, a.objectId, a.deployDigest).localeCompare(
          suiRowKey(b.network, b.objectId, b.deployDigest)
        )
      );
    },
    enabled: SHOW_SOLANA_SUI && (hasSavedSuiSplits || Boolean(SUI_PACKAGE_ID && suiAddr)),
    staleTime: 45_000,
  });

  const removeSolanaRow = (cluster: 'devnet' | 'mainnet', addr: string) => {
    removeDeployedSplit('solana', 'devnet', addr);
    removeDeployedSplit('solana', 'mainnet', addr);
    suppressOnChainSplit('solana', cluster, addr);
    setListVersion((v) => v + 1);
  };

  const removeSuiRow = (row: SuiDashRow) => {
    if (row.deployDigest) {
      removeDeployedSplit('sui', row.network, row.deployDigest);
    }
    const hideId = row.objectId ?? row.deployDigest ?? '';
    if (hideId) suppressOnChainSplit('sui', row.network, hideId);
    setListVersion((v) => v + 1);
  };

  const tagLine = (tags: ('saved' | 'wallet')[]) => {
    if (tags.includes('wallet') && tags.includes('saved')) return 'On-chain (creator) · also saved here';
    if (tags.includes('wallet')) return 'On-chain (tx sender = creator)';
    return 'Saved in this browser';
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {!isConnected && (
        <div className="space-y-10 pb-4">
          <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-base-200/80 via-base-100 to-base-200/40 px-6 py-10 md:px-10 md:py-14 shadow-lg">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-secondary/10 blur-3xl" />
            <div className="relative max-w-2xl mx-auto space-y-5">
              <div className="flex justify-center">
                <img
                  src={assetUrl(brand.heroLogoSrc)}
                  alt={brand.heroLogoAlt}
                  className="w-auto max-h-24 sm:max-h-28 md:max-h-32 max-w-[min(90vw,400px)] object-contain object-center"
                />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                {brand.dashboard.heroTitle}
              </h1>
              <p className="text-base opacity-85 leading-relaxed">{brand.dashboard.heroSubtitle}</p>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <ConnectButton />
                <Link to="/new" className="btn btn-outline btn-primary">
                  Create a split
                </Link>
                <Link to="/gas" className="btn btn-ghost">
                  Gas estimator
                </Link>
              </div>
              <p className="text-xs opacity-60 pt-2">Connect an EVM wallet to open your dashboard.</p>
              {brand.poweredBy && (
                <p className="text-xs opacity-55 pt-4 border-t border-neutral-700/40 text-center">
                  Powered by{' '}
                  <a
                    href={brand.poweredBy.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link link-hover"
                  >
                    {brand.poweredBy.linkLabel}
                  </a>
                </p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-neutral-700/50 bg-base-200/20 p-5">
            <h2 className="text-lg font-semibold mb-3">Supported Chains</h2>
            {supportedChains.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-neutral-700/50 bg-base-300/20 py-3">
                <div className="dash-chain-marquee">
                  {[...supportedChains, ...supportedChains].map((chain, index) => (
                    <div
                      key={`${chain.id}-${index}`}
                      className="inline-flex items-center gap-2 px-4 text-sm opacity-90 whitespace-nowrap"
                    >
                      <img src={chain.icon} alt={`${chain.name} logo`} className="w-5 h-5 rounded-full" loading="lazy" />
                      <span>{chain.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm opacity-70">Chain list loading…</p>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <article className="rounded-xl border border-neutral-700/60 bg-base-200/30 p-5 shadow-sm">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <span className="text-xl" aria-hidden>
                  ⟠
                </span>
                {brand.dashboard.marketingEvmsTitle}
              </h2>
              <p className="text-sm opacity-80 mt-2 leading-relaxed">{brand.dashboard.marketingEvmsBody}</p>
            </article>
            {/* Solana + Sui marketing cards — restore when SHOW_SOLANA_SUI
            <article>… Solana programs …</article>
            <article>… Sui Move …</article>
            */}
            <article className="rounded-xl border border-neutral-700/60 bg-base-200/30 p-5 shadow-sm">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <span className="text-xl" aria-hidden>
                  ⛽
                </span>
                {brand.dashboard.marketingGasTitle}
              </h2>
              <p className="text-sm opacity-80 mt-2 leading-relaxed">{brand.dashboard.marketingGasBody}</p>
            </article>
          </div>

          

          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-neutral-700/50 bg-base-200/20 px-5 py-4">
            <p className="text-sm opacity-80">{brand.dashboard.bottomCta}</p>
            <a
              href={brand.nav.secondaryHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-ghost"
            >
              {brand.dashboard.docLinkLabel}
            </a>
          </div>
        </div>
      )}

      {isConnected && (
        <>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-sm opacity-80 mt-1">{brand.dashboard.connectedSubtitle}</p>
            <details className="collapse collapse-arrow bg-base-200/25 border border-neutral-700/50 rounded-box mt-3">
              <summary className="collapse-title text-sm font-medium py-2 min-h-0">
                How this dashboard loads data
              </summary>
              <div className="collapse-content text-xs opacity-75 leading-relaxed space-y-2 pt-0">
                <p>
                  <span className="font-medium text-base-content/90">EVM</span> — Shows payment splitters where your
                  wallet is a payee. Balances and history are loaded from the network.
                </p>
              </div>
            </details>
          </div>
        </>
      )}

      {isConnected && !factory && (
        <div role="alert" className="alert alert-warning">
          <span>No factory address configured for this network. Coming Soon.</span>
        </div>
      )}

      {isConnected && factory && address && (
        <>
          {isLoading && <p className="text-sm opacity-70">Loading your splits…</p>}
          {!isLoading && unique.length === 0 && (
            <p className="text-sm opacity-70">
              No splits found for this address on the factory. Deploy one from "Create split" or use another network.
            </p>
          )}
          <div className="space-y-4">
            {unique.map((s) => (
              <SplitterPanel key={s} splitterAddress={s} explorerBaseUrl={explorer} />
            ))}
          </div>
        </>
      )}

      {SHOW_SOLANA_SUI &&
        isConnected &&
        (!SOLANA_PROGRAM_ID || !SOLANA_RPC_URL) &&
        mergedSolana.some((r) => r.tags.includes('saved')) && (
          <p className="text-xs opacity-60">
            Automatic lookup for your Solana wallet isn’t available in this deployment. Splits you’ve saved in this
            browser still appear below.
          </p>
        )}

      {SHOW_SOLANA_SUI && isConnected && mergedSolana.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold mt-4">Solana splits</h2>
          {mergedSolana.map(({ addr, cluster, tags }) => (
            <div
              key={`${cluster}-${addr}`}
              className="card bg-neutral text-neutral-content shadow-sm border border-neutral-700/80"
            >
              <div className="card-body gap-2 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs opacity-70">
                      Split PDA · <span className="uppercase">{cluster}</span> · {tagLine(tags)}
                    </p>
                    <a
                      href={
                        cluster === 'mainnet'
                          ? `https://explorer.solana.com/address/${addr}`
                          : `https://explorer.solana.com/address/${addr}?cluster=devnet`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-sm link link-primary break-all"
                    >
                      {addr}
                    </a>
                  </div>
                  <button type="button" className="btn btn-ghost btn-xs" onClick={() => removeSolanaRow(cluster, addr)}>
                    Remove
                  </button>
                </div>
                <SolanaSplitActivity pdaAddress={addr} cluster={cluster} />
              </div>
            </div>
          ))}
        </div>
      )}

      {SHOW_SOLANA_SUI && isConnected && !SUI_PACKAGE_ID && mergedSui.some((r) => r.tags.includes('saved')) && (
        <p className="text-xs opacity-60">
          Automatic lookup for your Sui wallet isn’t available in this deployment. Splits you’ve saved in this browser
          still appear below.
        </p>
      )}

      {SHOW_SOLANA_SUI && isConnected && SUI_PACKAGE_ID && suiAddr && suiDiscoverLoading && (
        <p className="text-sm opacity-70">Loading Sui splits for your wallet…</p>
      )}

      {SHOW_SOLANA_SUI && isConnected && mergedSui.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold mt-4">Sui splits</h2>
          {mergedSui.map((row) => {
            const key = `${row.network}-${row.objectId ?? row.deployDigest ?? ''}`;
            const base = row.network === 'mainnet' ? 'https://suiscan.xyz/mainnet' : 'https://suiscan.xyz/devnet';
            const primary = row.objectId ?? row.deployDigest ?? '';
            const href = row.objectId ? `${base}/object/${row.objectId}` : `${base}/tx/${row.deployDigest}`;
            return (
              <div
                key={key}
                className="card bg-neutral text-neutral-content shadow-sm border border-neutral-700/80"
              >
                <div className="card-body gap-2 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs opacity-70">
                        {row.objectId ? 'Split object' : 'Deploy transaction'} ·{' '}
                        <span className="uppercase">{row.network}</span> · {tagLine(row.tags)}
                      </p>
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-sm link link-primary break-all"
                      >
                        {primary}
                      </a>
                    </div>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => removeSuiRow(row)}>
                      Remove
                    </button>
                  </div>
                  <SuiSplitActivity
                    objectId={row.objectId}
                    deployDigest={row.objectId ? undefined : row.deployDigest}
                    network={row.network}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`
        .dash-chain-marquee {
          display: inline-flex;
          min-width: max-content;
          animation: dash-chain-marquee-scroll 28s linear infinite;
        }
        @keyframes dash-chain-marquee-scroll {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </div>
  );
}
